import { useMemo, useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';

import { formatSinceTime } from '@helpers';
import { is24HourFormat } from '@components/frame';
import { useFetchDevicesFiltered } from '@hooks/useFetchDevicesFiltered';
import { buildSettingsNavParams, type SettingsRootStack } from '@components/settings/v2/common/navigationParams';

interface DeviceLineItem {
  deviceId: string | null | undefined;
  name: string | null | undefined;
  bottom: string | null | undefined;
  value: string;
  onClick: () => void;
}

export interface UseManageDevicesMobileParams {
  rootStack?: SettingsRootStack;
}

export const useManageDevicesMobile = ({ rootStack = 'Settings' }: UseManageDevicesMobileParams = {}) => {
  const navigation = useNavigation();
  const { devices, loaded } = useFetchDevicesFiltered({ size: 500 });

  const navigateToDeviceDetails = useCallback(
    (deviceId: string) => {
      navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Devices', 'DeviceDetails', { id: deviceId }) as never);
    },
    [navigation, rootStack],
  );

  const navigateToAddDevice = useCallback(() => {
    navigation.navigate('AddDevice' as never);
  }, [navigation]);

  const filteredDevices = useMemo(() => {
    return devices.reduce<{ wallboard: DeviceLineItem[]; other: DeviceLineItem[] }>(
      (acc, d) => {
        const device: DeviceLineItem = {
          deviceId: d.deviceId,
          name: d.model,
          bottom: d.name,
          value: d.lastUsedOn ? formatSinceTime(d.lastUsedOn, is24HourFormat) : '',
          onClick: () => navigateToDeviceDetails(d.deviceId!),
        };
        if (d.deviceType === 'Wallboard') {
          acc.wallboard.push(device);
        } else {
          acc.other.push(device);
        }
        return acc;
      },
      { wallboard: [], other: [] },
    );
  }, [devices, navigateToDeviceDetails]);

  return {
    wallboard: filteredDevices.wallboard,
    other: filteredDevices.other,
    loaded,
    navigateToAddDevice,
  };
};
